import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { AddEventPopup } from '../../routes/schedule/AddEventPopup.js'
import type { Job } from './types.js'

/**
 * Booking a stop against this job — collect keys, a pickup, a drop-off.
 *
 * A stop is a `CalendarEvent` and never a `ScheduledVisit`: writing a visit to
 * record a five-minute key collection sets `lastVisitAt` and the Jobs list then
 * calls the job Scheduled. Decisions 45 and 67.
 *
 * It used to be a panel of its own on Work, listing the stops and offering the
 * button. The stops are rows in the activity column now, in time order beside
 * the bookings they hang off, so all that is left here is the button and the
 * form behind it.
 */
export function AddStopButton({ job, className }: { job: Job; className?: string }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { user } = useSession()
  const [adding, setAdding] = useState(false)

  const staff = useQuery(trpc.users.schedulable.queryOptions())
  const people = staff.data ?? []

  /*
   * Whose day the stop lands on. The signed-in person if they are on the
   * board, otherwise the first technician — the form's Who select is what
   * changes it, and a stop with nobody on it is not a stop.
   */
  const person =
    people.find((entry) => entry.id === user?.id) ??
    people[0] ??
    (user ? { id: user.id, name: user.name, colour: user.colour ?? '#64748b' } : null)

  const createEvent = useMutation(
    trpc.schedule.createEvent.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        void queryClient.invalidateQueries(trpc.schedule.pathFilter())
        setAdding(false)
      },
    }),
  )

  if (!person) return null

  return (
    <>
      <button type="button" className={className} onClick={() => setAdding(true)}>
        Add a stop
      </button>

      <AddEventPopup
        open={adding}
        person={adding ? person : null}
        people={people}
        defaultDate={new Date()}
        defaultType="TASK"
        defaultJobId={job.id}
        isPending={createEvent.isPending}
        error={createEvent.error ? errorMessage(createEvent.error) : null}
        onClose={() => setAdding(false)}
        onSave={(input) => {
          if (input.kind !== 'event') return
          const { kind: _kind, ...event } = input
          createEvent.mutate({ ...event, jobId: job.id, type: 'TASK' })
        }}
      />
    </>
  )
}
